import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { portfolioItems } from '../mock';

const MoreStories = ({ currentSlug }) => {
  const stories = portfolioItems.filter(
    (p) => p.storySlug && p.storySlug !== currentSlug
  );

  if (stories.length === 0) return null;

  return (
    <section
      data-testid="more-stories-section"
      className="relative bg-[#0a0a0a] py-24 lg:py-32 px-6 lg:px-12 border-t border-white/5 overflow-hidden"
    >
      {/* Ambient gold glow */}
      <div className="pointer-events-none absolute -top-32 left-1/3 w-96 h-96 rounded-full bg-[#c9a96e]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 right-1/3 w-96 h-96 rounded-full bg-[#c9a96e]/8 blur-3xl" />

      <div className="relative max-w-[1500px] mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-6 mb-8">
            <span className="block w-20 sm:w-28 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-20 sm:w-28 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>
          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">
            Continue the Journey
          </p>
          <h2
            className="font-serif italic text-white text-4xl md:text-5xl lg:text-6xl leading-tight"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            More Love Stories
          </h2>
          <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-7" />
        </div>

        {/* Carousel — horizontal snap-scroll on all sizes */}
        <div
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6 -mx-6 px-6 lg:mx-0 lg:px-0 lg:justify-center"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#c9a96e #0a0a0a' }}
        >
          {stories.map((story) => (
            <Link
              key={story.id}
              to={`/portfolio/${story.storySlug}`}
              data-testid={`more-story-card-${story.storySlug}`}
              className="group relative shrink-0 snap-center w-[88vw] sm:w-[60vw] md:w-[480px] lg:w-[520px] aspect-[4/5] overflow-hidden bg-black"
            >
              <img
                src={story.image}
                alt={story.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/20" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-transparent" />

              {/* Gold corner accents (animate in on hover) */}
              <span className="absolute top-5 right-5 w-8 h-8 border-t border-r border-[#c9a96e] opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />
              <span className="absolute bottom-5 left-5 w-8 h-8 border-b border-l border-[#c9a96e] opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />

              {/* Eyebrow */}
              <div className="absolute top-6 left-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#c9a96e]/60 bg-black/40 backdrop-blur-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#e8d5a8] animate-pulse" />
                  <span className="text-[#e8d5a8] text-[9px] tracking-[0.4em] uppercase">Wedding Story</span>
                </div>
              </div>

              {/* Title + location */}
              <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10">
                <p className="text-[#c9a96e] text-[10px] tracking-[0.5em] uppercase mb-3">
                  {story.category}
                </p>
                <h3
                  className="font-serif italic text-white text-3xl md:text-4xl leading-tight mb-3 transition-transform duration-500 group-hover:translate-x-1"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {story.title}
                </h3>
                <p className="text-white/60 text-xs tracking-[0.25em] mb-5">{story.location}</p>

                <div className="flex items-center gap-3">
                  <span className="block w-8 h-px bg-[#c9a96e] transition-all duration-500 group-hover:w-14" />
                  <span className="text-[#e8d5a8] text-[10px] tracking-[0.4em] uppercase inline-flex items-center gap-2">
                    View Story
                    <ArrowRight size={12} className="transition-transform duration-500 group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Back to all portfolio */}
        <div className="text-center mt-14">
          <Link
            to="/#portfolio"
            data-testid="more-stories-view-all"
            className="inline-block px-10 py-4 border border-white/20 text-white/80 text-[11px] tracking-[0.4em] uppercase hover:border-[#c9a96e] hover:text-[#c9a96e] transition-colors duration-500"
          >
            View Full Portfolio
          </Link>
        </div>
      </div>
    </section>
  );
};

export default MoreStories;
